import React, { useState } from 'react'
import PropTypes from 'prop-types'

import axios from '../../dataClient/dataClient'
import { LightBGContainer, FlexEndContainer, StyledDialog, FlexDialogContent, FlexColumn } from '../styles/Containers'
import { ErrorMessage, WarningMessage } from '../common/UserMessage'
import Button, { SecondaryButton } from '../common/Button'
import BodyText, { BoldFormTitle, MedBodyText, BoldSpan } from '../styles/Text'
import FileList from '../files/FileList'
import ResponseMessage from '../foiDocumentsAndResponse/ResponseMessage'
import foiAccessMethod, { ACCESS_METHOD_TYPE } from '../../util/foiAccessMethod'

function SubmitFOIResponseButton({
  foiId,
  fileList = [],
  finalResponseLetter = null,
  finalResponseLetterId = '',
  letters = [],
  responseAttachment = null,
  bills = [],
  accessMethod = 'DIGITAL',
  accessMethodConfig,
  onSuccess = () => {}
}) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const hasUnpaidBill = bills.some(bill => bill.status === 'WAITING')
  const requiresResponsePackage = foiAccessMethod.requiresResponsePackage(accessMethod)
  const deliveryWarning = foiAccessMethod.getDeliveryWarningPresentation(accessMethod, accessMethodConfig)

  const handleOpen = () => setIsVisible(true)
  function handleClose() {
    setIsVisible(false)
    setError(false)
  }

  function handleSendFOI() {
    setIsLoading(true)
    setError(null)

    if (finalResponseLetter || fileList.length > 0) {
      axios
        .post(`/foi/${foiId}/response`)
        .then(() => {
          setIsVisible(false)
          onSuccess()
        })
        .catch(e => {
          console.error('Failed to submit FOI response:', e)
          setError('There was an error submitting your response. Please try again.')
        })
        .finally(() => setIsLoading(false))
    } else {
      console.error('Failed to submit FOI response: missing one of final response letter or files.')
      setError('Your response must include at least one of: final response letter or files.')
      setIsLoading(false)
    }
  }

  return (
    <div>
      <Button onClick={handleOpen}>Submit FOI Response</Button>
      <StyledDialog maxwidth="900px" open={isVisible} onClose={handleClose} keepMounted={false}>
        <FlexDialogContent>
          <BoldFormTitle>Confirm FOI Submission</BoldFormTitle>
          <ResponseMessage
            foiId={foiId}
            letters={letters}
            finalResponseLetterId={finalResponseLetterId}
            responseAttachment={responseAttachment}
            accessMethod={accessMethod}
            view="response"
          />
          <FlexColumn gap="10px">
            <BodyText>
              {requiresResponsePackage
                ? 'The following files will be included in the response package:'
                : 'The following files will be sent in your response:'}
            </BodyText>
            <LightBGContainer padding="20px 30px 20px 15px">
              <FileList type="database" files={fileList} requestId={foiId} buttonAction="download" />
            </LightBGContainer>
          </FlexColumn>
          {(hasUnpaidBill || deliveryWarning) && (
            <FlexColumn gap="10px">
              {hasUnpaidBill && (
                <WarningMessage>
                  <MedBodyText>This FOI has an unpaid bill.</MedBodyText>
                </WarningMessage>
              )}
              {deliveryWarning && (
                <WarningMessage>
                  <MedBodyText>
                    The requestor selected “<BoldSpan>{deliveryWarning.label}</BoldSpan>”. {deliveryWarning.editableInstruction}
                  </MedBodyText>
                </WarningMessage>
              )}
            </FlexColumn>
          )}
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <FlexEndContainer>
            <SecondaryButton onClick={handleClose} disabled={isLoading}>
              Cancel
            </SecondaryButton>
            <Button onClick={handleSendFOI} loading={isLoading}>
              Submit FOI
            </Button>
          </FlexEndContainer>
        </FlexDialogContent>
      </StyledDialog>
    </div>
  )
}

SubmitFOIResponseButton.propTypes = {
  foiId: PropTypes.string.isRequired,
  fileList: PropTypes.array,
  finalResponseLetter: PropTypes.object,
  finalResponseLetterId: PropTypes.string,
  letters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      state: PropTypes.oneOf(['DRAFT', 'SENT']).isRequired
    })
  ),
  responseAttachment: PropTypes.object,
  bills: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      status: PropTypes.string
    })
  ),
  accessMethod: PropTypes.oneOf(Object.values(ACCESS_METHOD_TYPE)),
  accessMethodConfig: PropTypes.shape({
    digitalLabel: PropTypes.string,
    physicalLabel: PropTypes.string,
    otherActiveType: PropTypes.shape({
      type: PropTypes.oneOf([ACCESS_METHOD_TYPE.OTHER_DIGITAL, ACCESS_METHOD_TYPE.OTHER_PHYSICAL, ACCESS_METHOD_TYPE.OTHER_PICKUP]).isRequired,
      label: PropTypes.string.isRequired,
      pricingDetails: PropTypes.string
    })
  }),
  onSuccess: PropTypes.func
}

export default SubmitFOIResponseButton
